// Hydrates the Quick Assessor redux state from outside the page — used by the
// property table's row select and by the unified Search page's
// "Open in Quick Assessor" action.
import {
  clearFilters,
  resetForm,
  setAssessorValue,
  setBuildingAddr,
  setCID,
  setCIDS,
  setCIDValue,
  setContact,
  setContacts,
  setCoordinates,
  setCustomFields,
  setData,
  setExempt,
  setMarker,
  setMils,
  setOwnerMailing,
  setOwnerName,
  setParcelID,
  setPropertyDesc,
  setSearchStatus,
  setTransferID,
} from "../features/assessorSlice";
import {
  resetContacts,
  setResults,
  setSearchTerm,
  setSelectedContact,
  setSelectedContacts,
  setTempData,
} from "../features/contactSlice";
import { changeName, changeNotes, loadLegend } from "../features/cidSlice";
import {
  getCIDContacts,
  getCIDInfo,
  getCIDProperties,
  getCIDS,
} from "./cidService";
import { getContact } from "./contactService";
import { getProperty } from "./propertyService";
import { joinContactsToProperties } from "../utils/propertyJoin";
import {
  persistSelectedCid,
  persistSelectedProperty,
} from "../utils/assessorPersistence";

// Selects a CID: loads its info/legend, its joined property+contact rows, and
// its mils rate. Mirrors lookupTool's handleChangeCID.
export async function loadCIDIntoAssessor(dispatch, cid, cids = null) {
  dispatch(setCID(cid));
  dispatch(setContacts([]));
  dispatch(clearFilters());
  // Remember the CID for a page refresh; a new CID invalidates any previously
  // remembered property (callers rehydrate the property separately).
  persistSelectedCid(cid);
  persistSelectedProperty(null);

  const [cidData, res, resContacts, cidList] = await Promise.all([
    getCIDInfo(cid),
    getCIDProperties(cid),
    getCIDContacts(cid),
    cids?.length ? Promise.resolve(cids) : getCIDS(),
  ]);

  dispatch(changeNotes(cidData.notes));
  dispatch(changeName(cidData.name));
  dispatch(loadLegend(cidData.legend));
  dispatch(setData(joinContactsToProperties(res?.data, resContacts)));

  if (cidList) {
    // Always sync the store's CID list — callers may hydrate the assessor
    // before its own page has loaded CIDs (e.g. Search -> Open).
    dispatch(setCIDS(cidList));
    cidList.forEach((c) => {
      if (c.id === cid) {
        dispatch(setMils(c.mils));
      }
    });
  }
}

// Loads one property into the editor form (fields, contacts, exempt flag) and
// reveals the form body. Mirrors the property table's row-select behavior.
export async function loadPropertyIntoAssessor(dispatch, propertyId) {
  dispatch(resetContacts());
  dispatch(resetForm());
  dispatch(setTransferID(propertyId));
  // Remember the property so a page refresh reopens it in the editor.
  persistSelectedProperty(propertyId);

  const property = await getProperty(propertyId);
  dispatch(setParcelID(property.parcelID));
  dispatch(setOwnerName(property.owner_name));
  dispatch(setOwnerMailing(property.owner_mailing));
  dispatch(setCIDValue(property.cid_value));
  dispatch(setMarker(property.flag_color));
  dispatch(setCustomFields(property.custom_fields || {}));
  dispatch(setBuildingAddr(property.building_address));
  dispatch(setPropertyDesc(property.property_desc));
  dispatch(setAssessorValue(property.assessor_value));
  dispatch(
    setCoordinates({
      latitude: property.latitude ?? null,
      longitude: property.longitude ?? null,
    })
  );

  const contactIds = Array.isArray(property.contact_ids)
    ? property.contact_ids
    : property.contact_id
      ? [property.contact_id]
      : [];

  dispatch(setContacts(contactIds));
  dispatch(setSelectedContacts(contactIds));
  dispatch(setSelectedContact(contactIds.length ? contactIds[0] : null));

  if (contactIds.length) {
    const contactRows = (
      await Promise.all(contactIds.map((contactId) => getContact(contactId)))
    ).filter(Boolean);
    if (contactRows.length) {
      dispatch(setSearchTerm(contactRows[0].name || ""));
      dispatch(setTempData(contactRows[0]));
      dispatch(setResults(contactRows));
    } else {
      dispatch(setSearchTerm(""));
      dispatch(setTempData(null));
      dispatch(setResults([]));
    }
  } else {
    dispatch(setSearchTerm(""));
    dispatch(setTempData(null));
    dispatch(setResults([]));
    dispatch(setContact(null));
  }

  if (parseFloat(property.cid_value) === 0) {
    dispatch(setExempt(true));
  } else {
    dispatch(setExempt(false));
  }
  dispatch(setSearchStatus(2));
  return property;
}
